import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";
import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _debounce from "lodash/debounce";

var _BORDER_STYLE, _templateObject, _templateObject2, _templateObject3, _templateObject4;

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React, { useState, useCallback, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { darken } from 'polished';
import PropTypes from 'prop-types';
import { useRect } from '@reach/rect';
import { getRelativePopoverStyles } from '../../utils';
import { steel, mystic, lightSteel, whiteSmoke, mineShaft, poppyRed } from '../../styles/colors';
import Popover from '../Popover';
import { IconChevron } from '../Icons';
import SelectContext from './SelectContext';
import SelectOption from './SelectOption';
import SelectSearchInput from './SelectSearchInput';
var DARK = 'dark';
var LIGHT = 'light';
var NONE = 'none';
var BORDER_STYLE = (_BORDER_STYLE = {}, _defineProperty(_BORDER_STYLE, DARK, "1px solid ".concat(steel)), _defineProperty(_BORDER_STYLE, LIGHT, "1px solid ".concat(mystic)), _defineProperty(_BORDER_STYLE, "error", "1px solid ".concat(poppyRed)), _defineProperty(_BORDER_STYLE, NONE, 'none'), _BORDER_STYLE);
var PADDING_FROM_TOP = 8;
var BorderColorOption = PropTypes.oneOf(Object.keys(BORDER_STYLE));
export default function Select(props) {
  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isOpen = _useState2[0],
      setIsOpen = _useState2[1];

  var _useState3 = useState(''),
      _useState4 = _slicedToArray(_useState3, 2),
      currentValueDisplayName = _useState4[0],
      setCurrentValueDisplayName = _useState4[1];

  var _useState5 = useState(''),
      _useState6 = _slicedToArray(_useState5, 2),
      typeAheadSearch = _useState6[0],
      setTypeAheadSearch = _useState6[1];

  var debouncedSettypeAheadSearch = useCallback(_debounce(setTypeAheadSearch, 1000), []);

  var _useState7 = useState(false),
      _useState8 = _slicedToArray(_useState7, 2),
      isKeyboardModality = _useState8[0],
      setIsKeyboardModality = _useState8[1];

  var ariaLabel = props['aria-label'],
      value = props.value,
      id = props.id,
      labelledby = props.labelledby,
      children = props.children,
      onChange = props.onChange,
      placeholder = props.placeholder,
      border = props.border,
      disabled = props.disabled,
      error = props.error,
      style = props.style,
      triggerProps = props.triggerProps,
      listboxProps = props.listboxProps,
      containerProps = props.containerProps;
  var selectTriggerRef = useRef();
  var listboxRef = useRef();
  var listboxRect = useRect(listboxRef, isOpen);
  var triggerRect = useRect(selectTriggerRef, isOpen);
  var optionsRef = useRef(null);

  var _useState9 = useState(-1),
      _useState10 = _slicedToArray(_useState9, 2),
      activeDescendantIndex = _useState10[0],
      setActiveDescendantIndex = _useState10[1];

  var activeDescendant = useRef(null);
  var typeAheadToggleListbox = useRef(false);
  var focusBackToTrigger = useRef(false);
  var labelledByContentRef = useRef(ariaLabel);
  useEffect(function () {
    if (!labelledByContentRef.current) {
      var possibleLabel = document.querySelector("[for=\"".concat(id, "\"]"));
      var labelContent = possibleLabel && possibleLabel.textContent || '';
      labelledByContentRef.current = labelContent;

      if (process.env !== 'production' && !labelContent) {
        // eslint-disable-next-line no-console
        console.warn('Select should be associated to a Label or given an aria-label for accessibility.');
      }
    }
  }, []);
  useEffect(function () {
    if (isOpen) {
      listboxRef.current.focus();
    } else if (focusBackToTrigger.current) {
      selectTriggerRef.current.focus();
      focusBackToTrigger.current = false;
    }
  }, [isOpen]);
  useEffect(function () {
    optionsRef.current = [].slice.call(listboxRef.current.querySelectorAll('[role="option"]'));
  }, [children]);

  var setActiveDescendant = function setActiveDescendant(index) {
    activeDescendant.current = index > -1 ? optionsRef.current[index] : null;
    setActiveDescendantIndex(index);
  };

  useEffect(function () {
    var activeOptionIndex = optionsRef.current.findIndex(function (el) {
      return el.dataset.value === "".concat(value);
    });

    if (activeOptionIndex !== -1) {
      var firstNodeTextContent = [].slice.call(optionsRef.current[activeOptionIndex].childNodes).find(function (n) {
        return !!n.textContent;
      }).textContent;
      setCurrentValueDisplayName(firstNodeTextContent);

      if (isOpen) {
        setActiveDescendant(activeOptionIndex);
      } else {
        setActiveDescendant(-1);
      }
    } else {
      /* 
      In the case of Searchable Selects, the "selected option" may have
      become unrendered but we can retain the currentValueDisplayName if
      a value is still present. There's a potential bug if the value is 
      changed externally from a non-falsey value to another non-falsey value
      that isn't in the list of options; the Select will display the previous
      valueDisplayName incorrectly.
      */
      var preexistingValueDisplayName = !value ? '' : currentValueDisplayName;
      setCurrentValueDisplayName(preexistingValueDisplayName);
    }
  }, [value, isOpen, children]);
  var toggleListbox = useCallback(function () {
    var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : !isOpen;
    setIsOpen(state);
  }, [isOpen]);
  var onClick = useCallback(function (e) {
    if (!typeAheadToggleListbox.current) toggleListbox();
    typeAheadToggleListbox.current = false;
    onChange(e);
  }, [onChange, toggleListbox]);

  var findTypeAheadMatch = function findTypeAheadMatch(event) {
    if (event.key.length !== 1 || event.key === ' ') return;
    var newtypeAheadSearch = typeAheadSearch + event.key.toLowerCase();
    var searchExp = new RegExp(newtypeAheadSearch, 'gi');
    var nodeIndex = optionsRef.current.findIndex(function (item) {
      return item.textContent.toLowerCase().startsWith(newtypeAheadSearch);
    });
    if (nodeIndex === -1) nodeIndex = optionsRef.current.findIndex(function (item) {
      return item.textContent.match(searchExp);
    });

    if (nodeIndex !== -1) {
      optionsRef.current[nodeIndex].scrollIntoView({
        block: 'center'
      });

      if (!isOpen) {
        typeAheadToggleListbox.current = true;
        optionsRef.current[nodeIndex].click();
      } else {
        setActiveDescendant(nodeIndex);
      }
    } else if (nodeIndex === -1 && newtypeAheadSearch.length === 1) {
      newtypeAheadSearch = '';
    }

    setTypeAheadSearch(newtypeAheadSearch);
    debouncedSettypeAheadSearch('');
  };

  var handleListboxKeyboardAction = function handleListboxKeyboardAction(e) {
    if (e.target.tagName === 'INPUT') return;

    switch (e.key) {
      case 'ArrowUp':
        {
          e.preventDefault();
          var prevOptionIndex = Math.max(activeDescendantIndex - 1, 0);

          if (optionsRef.current[prevOptionIndex].offsetTop <= listboxRef.current.scrollTop + optionsRef.current[0].offsetTop && optionsRef.current[0].offsetTop > PADDING_FROM_TOP) {
            listboxRef.current.scrollTop -= optionsRef.current[0].offsetTop - 1;
          } else {
            optionsRef.current[prevOptionIndex].scrollIntoView({
              block: 'nearest'
            });
          }

          setActiveDescendant(prevOptionIndex);
          break;
        }

      case 'ArrowDown':
        {
          e.preventDefault();
          var nextOptionIndex = Math.min(activeDescendantIndex + 1, optionsRef.current.length - 1);
          optionsRef.current[nextOptionIndex].scrollIntoView({
            block: 'nearest'
          });
          setActiveDescendant(nextOptionIndex);
          break;
        }

      case ' ':
      case 'Enter':
        {
          focusBackToTrigger.current = true;

          if (activeDescendantIndex > -1) {
            optionsRef.current[activeDescendantIndex].click();
          }

          toggleListbox(false);
          break;
        }

      case 'Escape':
        focusBackToTrigger.current = true;
        toggleListbox(false);
        break;

      default:
        findTypeAheadMatch(e);
        break;
    }
  };

  var triggerKeyPress = function triggerKeyPress(e) {
    if (e.key === 'ArrowDown') {
      toggleListbox(true);
    } else {
      findTypeAheadMatch(e);
    }
  };

  var closeIfLeavingListbox = function closeIfLeavingListbox(e) {
    var relatedTarget = e.relatedTarget || document.activeElement; // activeElement for IE11 fix

    var targetIsWithinList = listboxRef.current.contains(relatedTarget);
    var targetIsTriggerButton = relatedTarget === selectTriggerRef.current;

    if (!targetIsTriggerButton) {
      toggleListbox(targetIsWithinList);
    }
  };

  var getListboxStyles = function getListboxStyles() {
    return _objectSpread(_objectSpread({
      visibility: isOpen ? null : 'hidden'
    }, getRelativePopoverStyles(triggerRect, listboxRect)), style.listbox);
  };

  var getIconColor = function getIconColor() {
    if (disabled) return lightSteel;
    if (error) return poppyRed;
    return steel;
  };

  return /*#__PURE__*/React.createElement(SelectContext.Provider, {
    value: {
      value: value,
      onClick: onClick,
      isOpen: isOpen
    }
  }, /*#__PURE__*/React.createElement(SelectContainer, _extends({
    onMouseEnter: function onMouseEnter() {
      return setIsKeyboardModality(false);
    },
    onKeyDown: function onKeyDown() {
      return setIsKeyboardModality(true);
    },
    style: style.container
  }, containerProps), /*#__PURE__*/React.createElement(SelectInput, _extends({
    id: id,
    "aria-haspopup": "listbox",
    "aria-label": "".concat(labelledByContentRef.current, " ").concat(currentValueDisplayName || placeholder),
    "aria-expanded": isOpen,
    onClick: function onClick() {
      return toggleListbox();
    },
    onKeyDown: triggerKeyPress,
    ref: selectTriggerRef,
    border: border,
    style: style.trigger,
    type: "button",
    disabled: disabled,
    placeholderShown: !currentValueDisplayName,
    "aria-invalid": error
  }, triggerProps), /*#__PURE__*/React.createElement(IconChevronStyled, {
    color: getIconColor(),
    direction: isOpen ? 'up' : 'down',
    "aria-hidden": "true",
    size: "large"
  }), currentValueDisplayName || placeholder), /*#__PURE__*/React.createElement(SelectListbox, _extends({
    style: getListboxStyles(),
    id: "".concat(id, "-listbox"),
    tabIndex: "0",
    role: "listbox",
    "aria-labelledby": labelledby,
    "aria-activedescendant": activeDescendant.current && activeDescendant.current.id,
    isKeyboardModality: isKeyboardModality,
    ref: listboxRef,
    onKeyDown: handleListboxKeyboardAction,
    onBlur: closeIfLeavingListbox
  }, listboxProps), children)));
}
Select.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  id: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  labelledby: PropTypes.string,
  children: PropTypes.node,
  onChange: PropTypes.func.isRequired,
  border: BorderColorOption,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  containerProps: PropTypes.shape({}),
  triggerProps: PropTypes.shape({}),
  listboxProps: PropTypes.shape({}),
  style: PropTypes.exact({
    container: PropTypes.object,
    trigger: PropTypes.object,
    listbox: PropTypes.object
  }),
  'aria-label': PropTypes.string
};
Select.defaultProps = {
  value: '',
  placeholder: '',
  labelledby: null,
  children: null,
  border: LIGHT,
  disabled: false,
  error: false,
  style: {
    trigger: {},
    listbox: {},
    container: {}
  },
  'aria-label': null,
  containerProps: {},
  triggerProps: {},
  listboxProps: {}
};
Select.Option = SelectOption;
Select.Search = SelectSearchInput;
var SelectContainer = styled.span(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  display: inline-block;\n  position: relative;\n"])));
var IconChevronStyled = styled(IconChevron)(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  bottom: 0;\n  margin: auto;\n  position: absolute;\n  right: 8px;\n  top: 0;\n"])));
var SelectInput = styled.button(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n  background: #fff;\n  border: ", ";\n  border-radius: 3px;\n  color: ", ";\n  font-family: inherit;\n  font-size: 1rem;\n  line-height: 1;\n  min-width: 10ch;\n  outline: none;\n  padding: 11px 40px 11px 12px;\n  text-align: left;\n  width: 100%;\n  button& {\n    /* Override sanitize.css min-height */\n    min-height: 40px;\n  }\n  &:focus,\n  &:hover {\n    background: ", ";\n  }\n  &:disabled {\n    background: ", ";\n    color: ", ";\n  }\n"])), function (props) {
  return BORDER_STYLE[props['aria-invalid'] && !props.disabled ? 'error' : props.border];
}, function (props) {
  if (props['aria-invalid']) return poppyRed;
  if (props.placeholderShown) return steel;
  return mineShaft;
}, whiteSmoke, whiteSmoke, lightSteel);
var SelectListbox = styled.div(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n  ", "\n\n  color: ", ";\n  max-height: 350px;\n  min-width: 100%;\n  overflow-y: auto;\n  padding: ", "px 0;\n\n  [aria-selected='true'] {\n    background: ", ";\n\n    :hover,\n    :focus {\n      background: ", ";\n    }\n  }\n\n  ", "\n"])), Popover, mineShaft, PADDING_FROM_TOP, mystic, darken(0.05, mystic), function (props) {
  // activedescendant should not be highlighted if isKeyboardModality is false
  return props['aria-activedescendant'] && props.isKeyboardModality && "\n      #".concat(props['aria-activedescendant'], " {\n        background: ").concat(whiteSmoke, ";\n        \n        &[aria-selected='true'] {\n          background: ").concat(darken(0.05, mystic), ";\n        }\n      }");
});